import axios from './axios'
import store from 'shared/redux/store'
import { showSuccess, showError } from 'shared/redux/app/reducer'
import { setIsUploading } from 'shared/redux/app/reducer'
import { createSelector } from 'reselect'
import { GlobalState } from 'shared/redux/reducer'
import { AxiosRequestConfig } from 'axios'
import { clearIndexedDB, base64ToBuffer, getMimeTypeFromFileName } from 'shared/utils/tools'
import getUploadLinkService from 'shared/utils/getUploadLink'
import { UploadResponse } from 'pages/Store/model/product'
import axiosInstance from 'axios'
import JSZip from 'jszip'

type UploadData = {
  entity: string
  entity_id: string
  property: string
  is_content_metadata?: boolean
  organization_id?: any
  token?: string | null
  token_type?: string | null
}

export const isUploadingSelector = createSelector(
  (state: GlobalState) => state.app.isUploading,
  isUploading => isUploading,
)

export default async function uploadService(
  base64string: string,
  params: UploadData,
  filename: string,
  fileType = 'image/png',
  isAwsPresigned = false,
  config?: AxiosRequestConfig,
) {
  try {
    const mime = base64string.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/)
    let mimeType = fileType
    if (mime && mime.length) {
      mimeType = mime[1]
    }

    if (isAwsPresigned && !config?.url) {
      const formData = new FormData()
      Object.entries(params).forEach(([key, val]) => formData.append(key, val))
      store.dispatch(setIsUploading(true))
      const fileBuffer = base64ToBuffer(base64string)
      if (fileType == 'application/zip') {
        const file = await fetch(base64string)
          .then(res => res.arrayBuffer())
          .then(buffer => new File([buffer], filename, { type: mimeType }))

        const jszip = new JSZip()
        const zip = await jszip.loadAsync(file)
        const path = `${params.entity}\\${params.property}\\${params.entity_id}\\${filename}`
        for (const fileNameInZip in zip.files) {
          const file = zip.files[fileNameInZip]
          if (!file.dir) {
            const fileMimeType = getMimeTypeFromFileName(fileNameInZip)
            const contentArrayBuffer = await file.async('arraybuffer')
            const fileInZipbuffer = Buffer.from(contentArrayBuffer)

            const uploadLinkResponse = await getUploadLinkService({}, path, fileMimeType, fileNameInZip)
            if (!uploadLinkResponse) {
              store.dispatch(setIsUploading(false))
              throw new Error('Unable to get upload link.')
            }

            const uploadAwsResp = await axiosInstance({
              url: uploadLinkResponse.file_path,
              method: 'PUT',
              ...(config || {}),
              headers: {
                'Content-Type': fileMimeType,
                'Content-Length': fileInZipbuffer.length,
                'Content-Disposition': 'inline',
              },
              data: fileInZipbuffer,
            })

            if (!uploadAwsResp) {
              store.dispatch(setIsUploading(false))
              throw new Error('Unable to upload file.')
            }
          }
        }
        const contentUrl = `${path}\\content\\index.html`
        formData.append('value', contentUrl)
        return axios({
          url: '/asset',
          method: 'PUT',
          ...(config || {}),
          data: formData,
        })
          .then(response => response)
          .finally(() => {
            store.dispatch(setIsUploading(false))
          })
      } else {
        const path = `${params.entity}\\${params.property}\\${params.entity_id}\\${filename}`
        const uploadLinkResponse = await getUploadLinkService(
          {},
          path,
          mimeType,
          '',
          params.token || '',
          params.token_type || '',
        )
        if (!uploadLinkResponse) {
          store.dispatch(setIsUploading(false))
          throw new Error('Unable to get upload link.')
        }
        return axiosInstance({
          url: uploadLinkResponse.file_path,
          method: 'PUT',
          ...(config || {}),
          headers: {
            'Content-Type': mimeType,
            'Content-Length': fileBuffer.length,
            'Content-Disposition': 'inline',
          },
          data: fileBuffer,
        })
          .then(async response => {
            formData.append('value', path)
            return axios({
              url: `/asset${params.token && params.token_type ? '/public' : ''}`,
              method: 'PUT',
              ...(config || {}),
              data: formData,
            })
          })
          .then(secondResponse => secondResponse)
          .finally(() => {
            store.dispatch(setIsUploading(false))
          })
          .catch(error => {
            store.dispatch(showError({ message: 'Error during file upload.' }))
            throw error
          })
      }
    } else {
      const formData = new FormData()
      Object.entries(params).forEach(([key, val]) => formData.append(key, val))
      const file = await fetch(base64string)
        .then(res => res.arrayBuffer())
        .then(buffer => new File([buffer], filename, { type: mimeType }))
      formData.append('file', file)
      if (fileType == 'application/zip' || fileType == 'application/pdf') {
        formData.append('file_type', fileType)
      } else if (fileType == 'image/png') {
        if (process.env.REACT_APP_ASSET_STORAGE != 's3') {
          clearIndexedDB('images')
        }
      }

      store.dispatch(setIsUploading(true))
      return axios({
        url: fileType == 'application/zip' ? '/file/upload/zip' : '/file/upload',
        method: 'POST',
        ...(config || {}),
        data: formData,
      })
        .then(response => {
          store.dispatch(setIsUploading(false))
          return response
        })
        .finally(() => {
          store.dispatch(setIsUploading(false))
        })
    }
  } catch (error) {
    store.dispatch(
      showError({
        message: 'Error during file upload',
      }),
    )
    store.dispatch(setIsUploading(false))
    throw error
  }
}
